import { Message, ToolCall } from "@cohesive/api-client";
import { dedupeToolCallsByIndex } from "../ToolCall/dedupeToolCallsByIndex";

export const CANVAS_EDIT_TOOL_NAME = "Tool_Toolbox_Canvas_CanvasEdit";

/** What a direct-placement tool drops on the canvas — drives the row label. */
export type CanvasPlacementKind = "image" | "video" | "file";

/**
 * Tools that place their output straight onto the canvas without going through
 * the canvas-edit tool. Mirrors the backend's `ToolNameToLoadingNodeKind`
 * (`shared/canvasflow/loading_node.go`) — the one map both placer paths gate
 * on — restricted to the entries that declare a `canvas_placement` input.
 * There is deliberately no `Tool_Toolbox_Openai_VideoGenerate`; it does not
 * exist.
 */
export const CANVAS_PLACEMENT_TOOL_KINDS: Record<string, CanvasPlacementKind> =
  {
    Tool_Toolbox_Googleai_ImageGenerate: "image",
    Tool_Toolbox_Googleai_ImageEdit: "image",
    Tool_Toolbox_Googleai_VideoGenerate: "video",
    Tool_Toolbox_Openai_ImageGenerate: "image",
    Tool_Toolbox_Openai_ImageEdit: "image",
    Tool_Toolbox_FileRecords_Create: "file",
  };

const CANVAS_EDIT_FALLBACK_LABEL = "Canvas edit";

const PLACEMENT_FALLBACK_LABELS: Record<CanvasPlacementKind, string> = {
  image: "Generated image",
  video: "Generated video",
  file: "Created file",
};

export interface CanvasAddGroup {
  /** Tool call id — stable React key and click target. */
  toolCallId: string;
  /** The call's `action_description`, when it has one. */
  description?: string;
  /**
   * What to label the row when the call carried no `action_description`,
   * resolved from the source tool so an image is not called a "canvas edit".
   */
  fallbackLabel: string;
  /** Node ids the call created, straight from the result. */
  addedIds: string[];
  /**
   * Node ids the call updated, derived from its arguments (see
   * {@link readTouchedNodeIds}) once its result reports an update
   * (see {@link reportsUpdates}).
   */
  updatedIds: string[];
  /** addedIds then updatedIds, deduped — the navigation target for this group. */
  nodeIds: string[];
}

export interface CanvasAddSummary {
  /** Sum of the per-call `addedIds` lengths — 1:1 with the tool calls, per the issue. */
  totalAdded: number;
  /** Sum of the per-call `updatedIds` lengths. */
  totalUpdated: number;
  /** Every touched node id across the turn, deduped, in call order. */
  nodeIds: string[];
  groups: CanvasAddGroup[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The canvas-edit call's result payload, or null when it has none to offer.
 * Every read of the result goes through here — both `created_node_ids` and the
 * `updated` count — so the two can never drift apart on what counts as a
 * completed call.
 *
 * Null means "no positive evidence", which is the only safe default: a call
 * that was never dispatched (stopped stream, interrupt, moderation) keeps its
 * arguments but never receives a result, and an errored call returns before
 * applying any operation at all. `completedWithoutError` rejects both.
 *
 * The payload shape is not typed on the client (the backend owns it), so it is
 * parsed defensively: `function.result` wins over the `parseMessageHistory`
 * stitch onto `call.result` (mirroring Tool_E2bBash), and the payload may be
 * the object itself or nested one level under `result` as an object or a JSON
 * string. When both levels carry the same key the nested one wins, being the
 * more specific of the two.
 */
function readCanvasEditPayload(call: ToolCall): Record<string, unknown> | null {
  if (!completedWithoutError(call)) return null;

  const data = call.function?.result?.data ?? call.result?.data;
  if (!isRecord(data)) return null;

  const nested = data.result;
  if (isRecord(nested)) return { ...data, ...nested };
  if (typeof nested === "string") {
    try {
      const parsed: unknown = JSON.parse(nested);
      if (isRecord(parsed)) return { ...data, ...parsed };
    } catch {
      // A non-JSON string result simply carries no node ids or counts.
    }
  }
  return data;
}

/**
 * Node ids the canvas-edit tool reports it created. `added` is deliberately
 * ignored — the id list is what we can navigate to.
 */
function readCreatedNodeIds(payload: Record<string, unknown> | null): string[] {
  const ids = payload?.created_node_ids;
  if (!Array.isArray(ids)) return [];
  return [...new Set(readStringIds(ids))];
}

/**
 * Whether the result reports that at least one update was actually applied —
 * the positive evidence the update derivation is gated on, mirroring
 * `created_node_ids` for creates and `completedWithoutError` for direct
 * placement. A canvas edit that resolved to zero operations returns early with
 * `{"message": "No changes to apply"}`, which carries no `updated` key at all,
 * and the backend increments its internal counter in the update branch whether
 * or not any operation was appended — so the count in the payload is the only
 * place that distinguishes "a node was addressed" from "a node was changed".
 */
function reportsUpdates(payload: Record<string, unknown> | null): boolean {
  const updated = payload?.updated;
  return typeof updated === "number" && updated > 0;
}

/** Non-empty strings out of a loosely-typed id array, order preserved. */
function readStringIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
}

function readArguments(call: ToolCall): Record<string, unknown> | undefined {
  const args = call.function?.arguments;
  return isRecord(args) ? args : undefined;
}

/** Node ids the call asked the backend to delete — they cannot be located. */
function readDeleteNodeIds(call: ToolCall): string[] {
  return readStringIds(readArguments(call)?.delete_node_ids);
}

/**
 * Node ids the call's own arguments name, which is where updates have to come
 * from: the result reports `updated` as a bare count and never collects the
 * ids (the backend increments `nodesUpdated` without gathering them). It can
 * afford not to, because it decides add-vs-update purely from whether the id
 * derived from these arguments already exists on the canvas — so a node cannot
 * be updated without being named here. Being named here is necessary but never
 * sufficient: the caller only consults this once `reportsUpdates` confirms the
 * result applied an update, since arguments alone say nothing about whether the
 * call ever ran.
 *
 * This mirrors the backend's `deriveNodeID`
 * (`shared/toolbox/kits/canvas/canvas_edit.go`), which resolves EXACTLY ONE
 * node id per input element from a strict precedence ladder and returns. The
 * ladder order below is load-bearing and must not be relaxed into a union of
 * the fields: when `entity` is present the backend never looks at `id`, so
 * counting both would report an untouched node as updated and send `Locate`
 * after it. Each element contributes at most one id:
 *
 * 1. `entity_id` — contributes NOTHING. The backend returns from this branch,
 *    so it also short-circuits `id`. It is the create path, and the tool spec
 *    warns that for system-placed entities (generated images/videos, ingested
 *    file records) the canvas node id differs from the entity's `data.id` — so
 *    treating it as a node id risks naming a node that does not exist. The
 *    cost is that re-submitting the same `entity_id` to refresh an
 *    already-placed entity is not counted as an update. That is an undercount,
 *    the safe direction, and the same tradeoff this module already makes
 *    everywhere else: better a missing row than a row `Locate` cannot reach.
 * 2. `entity` — a `generic` entity contributes nothing, because its node key is
 *    `genericNodeID(platform, suppliedID)`, a UUIDv5 hash of the platform and
 *    the supplied id rather than `data.id`, and that hash is not reproducible
 *    here. Otherwise `entity.data.id` is the node id. With no `data.id` the
 *    backend mints a fresh uuid, which makes the element a create that already
 *    shows up in `created_node_ids`, so it contributes nothing.
 * 3. `id` — the position/size update path, read only when neither of the above
 *    is present.
 *
 * Read defensively; `arguments` is loosely typed.
 */
function readTouchedNodeIds(call: ToolCall): string[] {
  const nodes = readArguments(call)?.nodes;
  if (!Array.isArray(nodes)) return [];

  const ids: string[] = [];
  for (const node of nodes) {
    if (!isRecord(node)) continue;

    if (typeof node.entity_id === "string" && node.entity_id.length > 0) {
      continue;
    }

    const entity = node.entity;
    if (isRecord(entity)) {
      if (entity.type === "generic") continue;
      const data = entity.data;
      if (isRecord(data) && typeof data.id === "string" && data.id.length > 0) {
        ids.push(data.id);
      }
      continue;
    }

    if (typeof node.id === "string" && node.id.length > 0) ids.push(node.id);
  }
  return [...new Set(ids)];
}

function readDescription(call: ToolCall): string | undefined {
  const description = (
    call.function?.arguments as { action_description?: unknown } | undefined
  )?.action_description;
  if (typeof description !== "string") return undefined;
  const trimmed = description.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Which direct-placement tool this is, if any — same name/execution-name pair the canvas-edit check reads. */
function readPlacementKind(call: ToolCall): CanvasPlacementKind | undefined {
  const { name, execution_tool_name: executionToolName } = call.function ?? {};
  return (
    (name ? CANVAS_PLACEMENT_TOOL_KINDS[name] : undefined) ??
    (executionToolName
      ? CANVAS_PLACEMENT_TOOL_KINDS[executionToolName]
      : undefined)
  );
}

/**
 * Whether the model actually asked for the output to land on the canvas.
 * `canvas_placement` is `.optional()` in every one of these tools' input
 * schemas and models routinely omit it, in which case the call still succeeds
 * and still returns an entity but places nothing — so this, not the presence
 * of a `reference_id`, is what makes the call a canvas add. `arguments` is
 * loosely typed, so it is read defensively like `readCreatedNodeIds` does.
 */
function hasCanvasPlacement(call: ToolCall): boolean {
  const args = call.function?.arguments as Record<string, unknown> | undefined;
  return isRecord(args) && isRecord(args.canvas_placement);
}

/**
 * Whether a placing call actually ran and came back clean. A placing call keeps
 * its `reference_id` whether or not it ever executed, so the id proves nothing
 * on its own: when a stream is stopped, interrupted, or moderation-flagged its
 * tool calls are never dispatched and the backend deletes the ghost node it had
 * already placed (`cleanupLoadingNodes` in
 * `services/neuraflow/domain/action/chat_completion.go`). Such a call carries no
 * result at all, so "no error" is not enough — only a call that actually
 * returned a result can be credited with a node. The canvas-edit path demands
 * the same thing and reuses this check through `readCanvasEditPayload`: no
 * result means no payload, hence neither `created_node_ids` nor an `updated`
 * count, rather than the mere absence of a failure standing in for evidence.
 *
 * Undercounting is the right failure direction: if a result has genuinely not
 * landed yet, showing nothing beats claiming a node that may not exist and
 * sending `Locate` after a node the canvas never got.
 *
 * Both result locations are checked the same way `readCreatedNodeIds` reads
 * them (`function.result` wins over the `parseMessageHistory` stitch onto
 * `call.result`), and an error in *either* location rejects the call, so a
 * result present in one place cannot mask a failure recorded in the other.
 */
function completedWithoutError(call: ToolCall): boolean {
  const results = [call.function?.result, call.result];
  if (results.some((result) => Boolean(result?.error))) return false;
  return isRecord(call.function?.result ?? call.result);
}

/**
 * Summarize the canvas nodes an assistant turn touched — created or updated —
 * one group per canvas-edit tool call and one per direct-placement call
 * (image/video/file generation, which place onto the canvas without going
 * through the canvas-edit tool). Returns null when the turn touched nothing
 * locatable, so callers can render nothing at all. A single pass keeps the
 * groups in message/tool-call order across both kinds.
 */
export function deriveCanvasAddSummary(
  messages: Message[],
): CanvasAddSummary | null {
  const groups: CanvasAddGroup[] = [];

  for (const message of messages) {
    // Dedupe first: a streamed call and its finalized re-send share one index,
    // and counting both would double the total (same helper the COT step count
    // uses, so the summary agrees with what renders).
    for (const call of dedupeToolCallsByIndex(message.tool_calls) ?? []) {
      const isCanvasEdit =
        call.function?.name === CANVAS_EDIT_TOOL_NAME ||
        call.function?.execution_tool_name === CANVAS_EDIT_TOOL_NAME;

      if (isCanvasEdit) {
        const payload = readCanvasEditPayload(call);
        const addedIds = readCreatedNodeIds(payload);
        // updated = touched − added − deleted, and only once the result itself
        // reports an update. Subtracting the created ids is what separates
        // "named an id that did not exist, so the backend created it" from a
        // real update; the result already says exactly which were creates.
        // Deleted nodes drop out because they are gone.
        //
        // `updated` is the GATE, never the count: it answers "did any update
        // land at all", and the row keeps reporting `updatedIds.length`. The
        // two are deliberately allowed to disagree — we skip `entity_id` and
        // generic entities, so the displayed number must stay equal to what
        // `Locate` can actually reach, not to what the backend tallied.
        const added = new Set(addedIds);
        const deleted = new Set(readDeleteNodeIds(call));
        const updatedIds = reportsUpdates(payload)
          ? readTouchedNodeIds(call).filter(
              (id) => !added.has(id) && !deleted.has(id),
            )
          : [];

        // A call that only deleted touched nothing we can navigate to.
        if (addedIds.length + updatedIds.length === 0) continue;

        groups.push({
          toolCallId: call.id,
          description: readDescription(call),
          fallbackLabel: CANVAS_EDIT_FALLBACK_LABEL,
          addedIds,
          updatedIds,
          nodeIds: [...new Set([...addedIds, ...updatedIds])],
        });
        continue;
      }

      // Direct placement adds exactly one node, and only when all three hold:
      // it is one of the placing tools, the model asked for placement, and the
      // call has the `reference_id` the backend used as the node id. The id is
      // minted on every tool call, placing or not, so it proves nothing alone.
      const placementKind = readPlacementKind(call);
      if (!placementKind) continue;
      if (!hasCanvasPlacement(call)) continue;
      if (!completedWithoutError(call)) continue;

      const nodeId = call.reference_id;
      if (typeof nodeId !== "string" || nodeId.length === 0) continue;

      // Always an add, never an update: each placing call mints a fresh
      // `reference_id` and the backend places a new node at it — including
      // `Tool_Toolbox_Googleai_ImageEdit`, which edits an image but lands the
      // result on a brand-new node, so it must not be reclassified.
      groups.push({
        toolCallId: call.id,
        description: readDescription(call),
        fallbackLabel: PLACEMENT_FALLBACK_LABELS[placementKind],
        addedIds: [nodeId],
        updatedIds: [],
        nodeIds: [nodeId],
      });
    }
  }

  if (groups.length === 0) return null;

  // Both totals count derived ids, not the result's reported `added`/`updated`
  // numbers, which can legitimately disagree (the excluded `entity_id` refresh
  // case above is one). What the row says must equal what `Locate` can reach.
  return {
    totalAdded: groups.reduce((sum, group) => sum + group.addedIds.length, 0),
    totalUpdated: groups.reduce(
      (sum, group) => sum + group.updatedIds.length,
      0,
    ),
    nodeIds: [...new Set(groups.flatMap((group) => group.nodeIds))],
    groups,
  };
}

/**
 * Whether a turn's canvas-add summary should render yet.
 *
 * A turn that is not the last one is settled history by definition — a later
 * turn already exists — so its count can never grow again and it always shows.
 *
 * The last turn is gated solely on the flow session being `ready`. Canvas nodes
 * are only ever added by tool calls, and tool calls only run while the session
 * is `processing`, so an idle session is already a complete guarantee that the
 * count cannot grow — announcing early is the only failure mode, and readiness
 * rules it out. Deliberately no turn-completion conjunct: requiring a settled
 * final answer message would hide the summary on successful tool-only turns,
 * which never produce one and are exactly the turns where the summary is the
 * only record of what happened. Session readiness is both the stronger and the
 * simpler signal.
 */
export function shouldShowCanvasAddSummary(args: {
  isLastTurn: boolean;
  isSessionReady: boolean;
}): boolean {
  return !args.isLastTurn || args.isSessionReady;
}
